/**
 * Integrytas Group - Data Privacy & Compliance (DPC)
 * Page: DPC.POLICY
 *
 * Configuration: field masking rules (enforced by the gateway), processing
 * purposes (for consent), and the subject-data map (which entities/columns hold
 * a subject's data, used by DSAR gather/erase).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import type { AuthSession } from '../../types/auth';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DPC.POLICY';

type Strategy = 'REDACT' | 'PARTIAL' | 'HASH' | 'NULLIFY';
type EraseMode = 'NULLIFY' | 'HASH' | 'REDACT';
interface RuleRow { dpc_field_rule_id: number; entity_name: string; field_name: string; strategy: Strategy; partial_keep: number; unmask_roles: unknown; is_active: 'Y' | 'N' }
interface PurposeRow { dpc_purpose_id: number; internal_code: string; name: string; is_active: 'Y' | 'N' }
interface MapRow { dpc_subject_map_id: number; subject_entity: string; data_entity: string; match_column: string; pii_columns: unknown; erase_mode: EraseMode; is_active: 'Y' | 'N' }

const RULE_GRID_COLS: GridColumnDef[] = [
  { key: 'entity_name',  header: 'Entity',       defaultWidth: 200 },
  { key: 'field_name',   header: 'Field',        defaultWidth: 170 },
  { key: 'strategy',     header: 'Strategy',     defaultWidth: 130 },
  { key: 'partial_keep', header: 'Keep',         defaultWidth: 80  },
  { key: 'unmask_roles', header: 'Unmask Roles', defaultWidth: 180 },
  { key: 'is_active',    header: 'Active',       defaultWidth: 80  },
];

export interface DpcPolicyPageProps { session: AuthSession }

export function DpcPolicyPage({ session }: DpcPolicyPageProps) {
  return (
    <PersonalizationProvider pageCode="DPC.POLICY" session={session}>
      <DpcPolicyPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DpcPolicyPageInner({ session }: DpcPolicyPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [purposes, setPurposes] = useState<PurposeRow[]>([]);
  const [maps, setMaps] = useState<MapRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets over the masking-rule logical columns.
  const [facets, setFacets] = useState<{ entity: string[]; strategy: string[]; active: string[] }>(
    { entity: [], strategy: [], active: ['Y'] },
  );
  // §17.B13: related-children expand/contract. A masking rule targets a single
  // entity/field and loads no child collections, so the disclosure renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [
    // TODO: wire relations
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [r, p, m] = await Promise.all([
        apiDb.from('dpc_field_rule').select('dpc_field_rule_id, entity_name, field_name, strategy, partial_keep, unmask_roles, is_active').order('entity_name'),
        apiDb.from('dpc_purpose').select('dpc_purpose_id, internal_code, name, is_active').order('internal_code'),
        apiDb.from('dpc_subject_map').select('dpc_subject_map_id, subject_entity, data_entity, match_column, pii_columns, erase_mode, is_active').order('subject_entity'),
      ]);
      if (r.error) throw r.error;
      setRules((r.data ?? []) as RuleRow[]); setPurposes((p.data ?? []) as PurposeRow[]); setMaps((m.data ?? []) as MapRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const addRule = async () => { await routedInsert(session, 'dpc_field_rule', { entity_name: '', field_name: '', strategy: 'REDACT', partial_keep: 4, unmask_roles: [], is_active: 'N' }); await reload(); };
  const saveRule = async (r: RuleRow, rolesText: string) => {
    let roles: unknown; try { roles = JSON.parse(rolesText); } catch { setError(t('dpc_policy.err.unmask_roles_json', 'Unmask roles must be a JSON array.')); return; }
    await routedUpdate(session, 'dpc_field_rule', 'dpc_field_rule_id', r.dpc_field_rule_id, { entity_name: r.entity_name, field_name: r.field_name, strategy: r.strategy, partial_keep: r.partial_keep, unmask_roles: roles, is_active: r.is_active });
  };
  const delRule = async (id: number) => { await routedDelete(session, 'dpc_field_rule', 'dpc_field_rule_id', id); setRules(rs => rs.filter(x => x.dpc_field_rule_id !== id)); };

  const addPurpose = async () => { await routedInsert(session, 'dpc_purpose', { internal_code: `PURPOSE_${Date.now()}`, name: 'New purpose', is_active: 'Y' }); await reload(); };
  const savePurpose = async (p: PurposeRow) => { await routedUpdate(session, 'dpc_purpose', 'dpc_purpose_id', p.dpc_purpose_id, { internal_code: p.internal_code.toUpperCase(), name: p.name, is_active: p.is_active }); };
  const delPurpose = async (id: number) => { await routedDelete(session, 'dpc_purpose', 'dpc_purpose_id', id); setPurposes(ps => ps.filter(x => x.dpc_purpose_id !== id)); };

  const addMap = async () => { await routedInsert(session, 'dpc_subject_map', { subject_entity: '', data_entity: '', match_column: '', pii_columns: [], erase_mode: 'NULLIFY', is_active: 'Y' }); await reload(); };
  const saveMap = async (m: MapRow, piiText: string) => {
    let pii: unknown; try { pii = JSON.parse(piiText); } catch { setError(t('dpc_policy.err.pii_columns_json', 'PII columns must be a JSON array.')); return; }
    await routedUpdate(session, 'dpc_subject_map', 'dpc_subject_map_id', m.dpc_subject_map_id, { subject_entity: m.subject_entity, data_entity: m.data_entity, match_column: m.match_column, pii_columns: pii, erase_mode: m.erase_mode, is_active: m.is_active });
  };
  const delMap = async (id: number) => { await routedDelete(session, 'dpc_subject_map', 'dpc_subject_map_id', id); setMaps(ms => ms.filter(x => x.dpc_subject_map_id !== id)); };

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      entity:   uniq(rules.map(r => r.entity_name)).map(v => ({ value: v, label: v })),
      strategy: uniq(rules.map(r => r.strategy)).map(v => ({ value: v, label: v })),
      active:   [{ value: 'Y', label: t('dpc_policy.boolean.on', 'On') }, { value: 'N', label: t('dpc_policy.boolean.off', 'Off') }],
    };
  }, [rules, t]);

  const facetsActive = facets.entity.length + facets.strategy.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ entity: [], strategy: [], active: [] }); };

  const filteredRules = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // OR within a facet, AND across facets.
    const matchesFacets = (r: RuleRow) =>
      (facets.entity.length   === 0 || facets.entity.includes(r.entity_name)) &&
      (facets.strategy.length === 0 || facets.strategy.includes(r.strategy)) &&
      (facets.active.length   === 0 || facets.active.includes(r.is_active));
    const base = (!q ? rules : rules.filter(r =>
      r.entity_name.toLowerCase().includes(q) || r.field_name.toLowerCase().includes(q) || r.strategy.toLowerCase().includes(q)));
    return base.filter(matchesFacets);
  }, [rules, filter, facets]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('dpc_policy.title', 'Privacy Policy')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('dpc_policy.filter_placeholder', 'Filter masking rules…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter label={t('dpc_policy.col.entity', 'Entity')}     options={facetOptions.entity}   selected={facets.entity}   onChange={v => setFacets(f => ({ ...f, entity: v }))} />
        <FacetFilter label={t('dpc_policy.col.strategy', 'Strategy')} options={facetOptions.strategy} selected={facets.strategy} onChange={v => setFacets(f => ({ ...f, strategy: v }))} />
        <FacetFilter label={t('dpc_policy.col.active', 'Active')}     options={facetOptions.active}   selected={facets.active}   onChange={v => setFacets(f => ({ ...f, active: v }))} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('dpc_policy.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('dpc_policy.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('dpc_policy.error_banner', "Couldn't complete the last privacy-policy operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reload()}
          details_label={t('common.details', 'Details')}
        />
      )}
      <div className="flex-1 overflow-auto p-3">
        <div className="mb-4 rounded border border-csm-border bg-white p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('dpc_policy.section.field_masking', 'Field Masking Rules')}</span>
            <button onClick={addRule} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('dpc_policy.add', '+ Add')}</button>
          </div>
          <PersonalizedGrid componentKey="grid" columns={RULE_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className={GRID_TABLE_CLASS}>
                <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-2 py-1" title={t('dpc_policy.col.related', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                    ))}
                    <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('dpc_policy.col.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRules.map(r => {
                    const i = rules.indexOf(r);
                    return (
                      <Fragment key={r.dpc_field_rule_id}>
                      <tr>
                        <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton
                            open={detail.isOpen(r.dpc_field_rule_id)}
                            hasChildren={hasRelated(r.dpc_field_rule_id)}
                            onToggle={() => detail.toggle(r.dpc_field_rule_id)}
                            expandLabel={t('dpc_policy.related.expand', 'Show related records')}
                            collapseLabel={t('dpc_policy.related.collapse', 'Hide related')}
                            tooltip={t('dpc_policy.related.tooltip', 'Show related records')}
                          />
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                              style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'entity_name' && <input value={r.entity_name} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, entity_name: e.target.value } : x))} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].unmask_roles ?? []))} placeholder={t('dpc_policy.placeholder.entity', 'entity')} className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-xs" />}
                            {col.key === 'field_name' && <input value={r.field_name} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, field_name: e.target.value } : x))} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].unmask_roles ?? []))} placeholder={t('dpc_policy.placeholder.field', 'field')} className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-xs" />}
                            {col.key === 'strategy' && <select value={r.strategy} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, strategy: e.target.value as Strategy } : x))} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].unmask_roles ?? []))} className="h-7 w-full rounded border border-csm-border px-1 text-csm-xs">{['REDACT', 'PARTIAL', 'HASH', 'NULLIFY'].map(s => <option key={s} value={s}>{s}</option>)}</select>}
                            {col.key === 'partial_keep' && (r.strategy === 'PARTIAL' ? <input type="number" value={r.partial_keep} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, partial_keep: Number(e.target.value) } : x))} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].unmask_roles ?? []))} className="h-7 w-14 rounded border border-csm-border px-1 text-csm-xs" title={t('dpc_policy.tip.keep_last_n', 'keep last N')} /> : <span className="text-csm-2xs text-csm-watermark">—</span>)}
                            {col.key === 'unmask_roles' && <input defaultValue={JSON.stringify(r.unmask_roles ?? [])} onBlur={e => void saveRule(rules[i], e.target.value)} placeholder='["ROLE"]' className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-2xs" title={t('dpc_policy.tip.unmask_roles_json', 'unmask roles JSON')} />}
                            {col.key === 'is_active' && <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={r.is_active === 'Y'} onChange={e => { setRules(rs => rs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveRule(rules[i], JSON.stringify(rules[i].unmask_roles ?? []))} /> {t('dpc_policy.on', 'on')}</label>}
                          </td>
                        ))}
                        <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                          {canDelete && (<button onClick={() => void delRule(r.dpc_field_rule_id)} className="text-csm-danger text-csm-xs">{t('dpc_policy.del', 'del')}</button>)}
                        </td>
                      </tr>
                      {detail.isOpen(r.dpc_field_rule_id) && (
                        <tr>
                          <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                            <RelatedChildrenPanel groups={buildRelatedGroups(r.dpc_field_rule_id)} session={session} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {filteredRules.length === 0 && (
                    <tr><td colSpan={visibleCols.length + 2} className="py-8 text-center">
                      <EmptyState
                        variant={anyFilterActive ? 'no-match' : 'no-data'}
                        title={t('dpc_policy.empty.masking_rules', 'No masking rules.')}
                        action={anyFilterActive ? undefined : <button onClick={addRule} className="mt-2 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('dpc_policy.add', '+ Add')}</button>}
                      />
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </div>

        <Section title={t('dpc_policy.section.processing_purposes', 'Processing Purposes')} onAdd={addPurpose}>
          {purposes.map((p, i) => (
            <div key={p.dpc_purpose_id} className="mb-1 flex items-center gap-1">
              <input value={p.internal_code} onChange={e => setPurposes(ps => ps.map((x, j) => j === i ? { ...x, internal_code: e.target.value.toUpperCase() } : x))} onBlur={() => void savePurpose(purposes[i])} className="h-7 w-48 rounded border border-csm-border px-1 font-mono text-csm-xs" />
              <input value={p.name} onChange={e => setPurposes(ps => ps.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} onBlur={() => void savePurpose(purposes[i])} className="h-7 w-56 rounded border border-csm-border px-1 text-csm-xs" />
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={p.is_active === 'Y'} onChange={e => { setPurposes(ps => ps.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void savePurpose(purposes[i])} /> {t('dpc_policy.on', 'on')}</label>
              {canDelete && (<button onClick={() => void delPurpose(p.dpc_purpose_id)} className="text-csm-danger text-csm-xs">{t('dpc_policy.del', 'del')}</button>)}
            </div>
          ))}
        </Section>

        <Section title={t('dpc_policy.section.subject_data_map', 'Subject Data Map (DSAR)')} onAdd={addMap}>
          {maps.map((m, i) => (
            <div key={m.dpc_subject_map_id} className="mb-1 flex flex-wrap items-center gap-1">
              <input value={m.subject_entity} onChange={e => setMaps(ms => ms.map((x, j) => j === i ? { ...x, subject_entity: e.target.value } : x))} onBlur={() => void saveMap(maps[i], JSON.stringify(maps[i].pii_columns ?? []))} placeholder={t('dpc_policy.placeholder.subject_entity', 'subject entity')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />
              <input value={m.data_entity} onChange={e => setMaps(ms => ms.map((x, j) => j === i ? { ...x, data_entity: e.target.value } : x))} onBlur={() => void saveMap(maps[i], JSON.stringify(maps[i].pii_columns ?? []))} placeholder={t('dpc_policy.placeholder.data_entity', 'data entity')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />
              <input value={m.match_column} onChange={e => setMaps(ms => ms.map((x, j) => j === i ? { ...x, match_column: e.target.value } : x))} onBlur={() => void saveMap(maps[i], JSON.stringify(maps[i].pii_columns ?? []))} placeholder={t('dpc_policy.placeholder.match_col', 'match col')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />
              <input defaultValue={JSON.stringify(m.pii_columns ?? [])} onBlur={e => void saveMap(maps[i], e.target.value)} placeholder='["col1","col2"]' className="h-7 w-44 rounded border border-csm-border px-1 font-mono text-csm-2xs" title={t('dpc_policy.tip.pii_columns_json', 'PII columns JSON')} />
              <select value={m.erase_mode} onChange={e => setMaps(ms => ms.map((x, j) => j === i ? { ...x, erase_mode: e.target.value as EraseMode } : x))} onBlur={() => void saveMap(maps[i], JSON.stringify(maps[i].pii_columns ?? []))} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                {['NULLIFY', 'HASH', 'REDACT'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {canDelete && (<button onClick={() => void delMap(m.dpc_subject_map_id)} className="text-csm-danger text-csm-xs">{t('dpc_policy.del', 'del')}</button>)}
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 rounded border border-csm-border bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        <button onClick={onAdd} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('dpc_policy.add', '+ Add')}</button>
      </div>
      {children}
    </div>
  );
}
